import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import closeIcon from '../Assets/images/close_red.png';
import InsightDisplay from './InsightDisplay';
import { createRequestData } from '../Utility/apiHelper';

const BusinessIntelligenceDialog = ({ open, handleClose }) => {
  const currentUser = useSelector(state => state?.owner?.currentuser);
  const clientId = currentUser?.client?.id;
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [open]);

  const handleSendQuery = async () => {
    if (!query.trim() || loading) return;

    const userMessage = query.trim();
    setQuery('');
    setError('');
    
    // Add user message to chat
    const newMessages = [...messages, { type: 'user', content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      if (!clientId) {
        setError('Client ID is required. Please ensure you are logged in.');
        setLoading(false);
        return;
      }

      const apiUrl = process.env.REACT_APP_AIML_API_URL || 'http://localhost:8000';
      const response = await createRequestData(
        { 
          query: userMessage,
          client_id: clientId
        },
        `${apiUrl}/insights`
      );

      const { insights } = response.data;

      // Add assistant response with structured data
      setMessages([
        ...newMessages,
        {
          type: 'assistant',
          userQuery: userMessage,
          insights: insights || 'No insights available',
        },
      ]);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to get insights';
      setError(errorMsg);
      setMessages([
        ...newMessages,
        {
          type: 'error',
          content: `Error: ${errorMsg}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleResetHistory = async () => {
    try {
      const apiUrl = process.env.REACT_APP_AIML_API_URL || 'http://localhost:8000';
      await createRequestData({},`${apiUrl}/reset-history`);
      setMessages([]);
      setError('');
    } catch (err) {
      console.error('Failed to reset history:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuery();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6">Business Intelligence</Typography>
        <Box>
          <IconButton
            onClick={handleResetHistory}
            size="small"
            title="Reset Conversation"
            sx={{ mr: 1 }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleClose} size="small">
            <img
              src={closeIcon}
              alt="close"
              style={{ height: '35px', cursor: 'pointer' }}
            />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f5f5f5',
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Ask me anything about your business data
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Try questions like:
            </Typography>
            <Box component="ul" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography component="li" variant="body2">
                "How many leads do we have?"
              </Typography>
              <Typography component="li" variant="body2">
                "What is the total revenue this month?"
              </Typography>
              <Typography component="li" variant="body2">
                "Show me leads by source"
              </Typography>
            </Box>
          </Box>
        )}

        <Stack spacing={2}>
          {messages.map((message, index) => (
            <Box key={index}>
              {message.type === 'user' ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    <Typography variant="body1">{message.content}</Typography>
                  </Paper>
                </Box>
              ) : message.type === 'error' ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {message.content}
                </Alert>
              ) : (
                <InsightDisplay
                  userQuery={message.userQuery}
                  insight={message.insights}
                />
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                  Thinking...
                </Typography>
              </Paper>
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '10px 20px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ask a question about your business data..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          inputRef={inputRef}
          size="small"
          sx={{ mr: 1 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendQuery}
          disabled={!query.trim() || loading}
          startIcon={<SendIcon />}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessIntelligenceDialog;

